import DataState from "./DataState";
import NumberUtil from "@plannel/utils/number-util";
import debug from "@plannel/utils/debug-util";
import GridExportSetting from "./GridExportSetting"

import {isEmpty, isEqual, isNil, isNull, isUndefined} from "lodash";

import p13nService from "@plannel/services/system/p13n-service";
import {grey, lightBlue, red} from "@mui/material/colors";
import GridUtils from "./GridUtils";
import dateUtils from "@plannel/utils/date-util";
import GridDatePicker from "@plannel/components/GridDatePicker";

const PASTE = "paste";
const OBJECT = "object";
const UNDO = "undo";
const REDO = "redo";
const SCOPE_RESTORE_SOURCES = new Set([PASTE, UNDO, REDO]); // 값이 editor UI 없이 직접 반영되는 소스 (ag-grid KEEP_EDITOR_SOURCES와 동일 계열)

const excelStyles =
[
  {
    id: "header",
    alignment: {
      vertical: "Center",
    },
    interior: {
      color: grey[100],
      pattern: "Solid",
    },
    font: {
      bold: true,
      size: 13,
    },
    borders: {
      borderBottom: {
        color: grey[900],
        lineStyle: "Continuous",
        weight: 1,
      },
    },
  },
  {
    id: "alertBackground",
    interior: {
      color:  red[100],
      pattern: "Solid",
    },
  },
  {
    id: "editableBackgroud",
    interior: {
      color: lightBlue[100],
      pattern: "Solid",
    },
  },
  {
    id: "greyBackground",
    interior: {
      color: grey[300],
      pattern: "Solid",
    },
  },
  {
    id: 'stringFormat',
    dataType: 'String'
  },
  { id: 'stringType', dataType: 'String' },
  { id: "protectType", dataType: "String", protection: {protected: false}},
  { id: 'flexibleDecimalType', numberFormat: {format: "#0.#####"}}
];

export const processCellForClipboard = (params) => {
  let value = params?.value;
  const column = params?.column;
  const colDef = column?.getColDef?.();
  if (!colDef) {
    return value;
  }

  const {cellRenderer, valueFormatter, cellDataType} = colDef;
  const renderer = valueFormatter || cellRenderer;
  const colId = column?.getColId?.() ?? colDef?.colId ?? colDef?.field ?? "";

  // auto group 열은 ag-grid 내부 formatter가 clipboard params에서 내부 속성(pivotValueColumn 등)을
  // 기대하다가 터질 수 있어 이미 계산된 값을 사용한다.
  if (typeof colId === "string" && colId.startsWith("ag-Grid-AutoColumn")) {
    return params?.valueFormatted ?? value ?? params?.node?.key;
  }

  if (typeof value === OBJECT && !isNil(value) && typeof renderer !== "string" && renderer) {
    value = renderer(params);
  }

  const formattedDataType = ["text", "dateString", false];
  if (formattedDataType.includes(cellDataType) && valueFormatter) {
    if (typeof valueFormatter !== "function") {
      return params?.valueFormatted ?? value;
    }

    let formattedValue;
    try {
      formattedValue = valueFormatter(params);
    } catch (e) {
      console.warn("[DefaultGridSetting] clipboard valueFormatter fallback", e);
      return params?.valueFormatted ?? value;
    }

    if (cellDataType === false && isEmpty(formattedValue)) {
      return value;
    }

    return formattedValue;
  }

  return value;
}

export const gridPaste = (e) => {
  const { field, cellRenderer, valueFormatter, cellEditor, cellEditorParams, filterOption, context } = e.colDef;
  const pasteOption = context?.pasteOption;
  const renderer = valueFormatter || cellRenderer;

  const source = e?.source;
  let value = e?.value;
  const dateType = cellEditorParams?.dateType;
  const isGridDatePicker = (cellEditor === GridDatePicker) || !isNil(dateType); // GridDatePicker 일치확인 또는 dateType 존재 여부 확인

  let isDate = false;
  if (isGridDatePicker) {
    isDate = dateUtils.isDateString(value);
  }

  try {
    if (source === PASTE && isDate) {
      value = value.trim();

      if (dateType === "month") {
        const yearMonth = value.substring(0, 7);
        if (/^\d{4}-(0[1-9]|1[0-2])$/.test(yearMonth)) {
          const isFirstDay = cellEditorParams?.isFirstDay;

          const [year, month] = yearMonth.split('-').map(Number);
          const dateValue = isFirstDay
            ? new ZDate(year, month - 1, 1)
            : new ZDate(year, month, 0);

          e.newValue = dateValue;
          e.value = dateValue;
          e.data[field] = dateValue;
          return;
        }
      }

      const normalizedDateValue = /^\d{4}-(0[1-9]|1[0-2])$/.test(value) ? `${value}-01` : value;
      const dateValue = new ZDate(normalizedDateValue);

      e.newValue = dateValue;
      e.value = dateValue;
      e.data[field] = dateValue;
    } else if (source === PASTE && renderer && cellEditorParams) {
      let isChange = false;
      let objectValues = cellEditorParams.values;

      if (isNil(objectValues)) {
        objectValues = pasteOption;
        if (isNil(objectValues)) {
          objectValues = filterOption;
        }
      } else if (typeof objectValues === "function") {
        objectValues = objectValues(e);
      }

      if (objectValues) {
        for (const objectValue of objectValues) {
          const wrapperValue = { value: objectValue };

          if (value === renderer(wrapperValue)) {
            e.newValue = objectValue;
            e.value = objectValue;
            e.data[field] = objectValue;
            isChange = true;
            break;
          } else if (isEqual(value, objectValue)) {
            e.newValue = value;
            e.value = value;
            e.data[field] = value;
            isChange = true;
            break;
          }
        }

        if(!isChange) {
          e.data[field] = e.oldValue;
          e.api.redrawRows({rowNodes: [e.node]});
        }
      }
    }
  } finally {
    if (SCOPE_RESTORE_SOURCES.has(source)) {
      const { api, rowIndex, column, rowPinned } = e;
      api.stopEditing(true); // paste/undo/redo로 값 이미 반영됨 → cancel=true로 편집상태(팝업 에디터)만 정리, 재커밋 방지
      api.setFocusedCell(rowIndex, column.getColId(), rowPinned); // stopEditing으로 잃은 grid focus(scope) 복원 → 연속 Undo/Redo(Ctrl+Z/Y) 유지
    }
  }
}

const onCellValueChanged = (e) => {
  gridPaste(e);
  DataState.setDataState(e);
}

const isEditable = params => {
  if (params.colDef.editable) {
    const { editable } = params.colDef;

    return (typeof editable === "function" && editable(params)) || isEqual(editable, true);
  }

  return false;
}

const checkboxClickEvent = (e, params) => {
  if (isEditable(params)) {
    params.node.setDataValue(params.column, !params.value);
  } else {
    e.preventDefault();
  }
}

const getMainMenuItems = (params, others) => {
  const { viewName, columnDefs, gridId = "grid" } = others;

  const menuItems = [...params.defaultItems];
  if (viewName) {
    const resetColumns = {
      name: 'Reset Columns',
      action: () => {
        params.api.resetColumnState();
        const saveData = { pageCd: viewName, configObj: { [gridId]: [] } };
        p13nService.upsert(saveData)
          .catch(e => {
            debug(e);
          })
      },
    };
    const saveColumns = {
      name: 'Save Columns',
      action: () => {
        const allColumnState = params.api.getColumnState();

        const columnState = [];
        // children 있는 항목에는 적용할 수 없음
        if (columnDefs) {
          const colIds = columnDefs.map(columnDef => columnDef?.colId ?? columnDef.field);
          for (const state of allColumnState) {
            const { colId } = state;
            if (colIds.indexOf(colId) > -1) {
              columnState.push(state);
            }
          }
        }

        let state = isEmpty(columnState) ? allColumnState : columnState;
        state = state.filter(col => !col?.colId?.includes(GridUtils.IGNORE_P13N_COLLID)); /* Ignore IGNORE_P13N_COLLID */

        const saveData = { pageCd: viewName, configObj: { [gridId]: state } };
        p13nService.upsert(saveData)
          .catch(e => {
            debug(e);
          })
      },
    };

    const resetColumnsIdx = menuItems.indexOf("resetColumns");
    menuItems.splice(resetColumnsIdx, 1, resetColumns, saveColumns);
  }

  return menuItems;
}

const getContextMenuItems = (params) => {
  const defaultContextMenuItems = [];
  
  if (isNil(params?.defaultItems)) {
    return defaultContextMenuItems;
  }

  for (const item of params?.defaultItems) {
    if (item !== "paste") {
      defaultContextMenuItems.push(item);
    }
  }
  
  return defaultContextMenuItems;
}

const stringifyForCompare = (value) => {
  try {
    const str = JSON.stringify(value, (key, val) => (typeof val === "bigint" ? val.toString() : val));
    return str ?? "";
  } catch (e) {
    return String(value);
  }
};

const nullSafeComparator = (valueA, valueB, isDescending) => {
  const isNullA = valueA === null || valueA === undefined || valueA === "";
  const isNullB = valueB === null || valueB === undefined || valueB === "";
  
  if (isNullA && isNullB) return 0;
  
  if (isNullA) return isDescending ? -1 : 1;
  if (isNullB) return isDescending ? 1 : -1;
  
  if (valueA === valueB) return 0;

  // 화이트페이지 방지
  if (typeof valueA === "object" || typeof valueB === "object") {
    const strA = stringifyForCompare(valueA);
    const strB = stringifyForCompare(valueB);

    if (strA === strB) return 0;

    return strA > strB ? 1 : -1;
  }

  return valueA > valueB ? 1 : -1;
};

const DefaultGridSetting = ({ title = "", ...others }) => {
  const userData = JSON.parse(localStorage.getItem("user"));
  const currencySymbol = userData?.currencySymbol ?? "$";

  return {
    theme: "legacy",
    columnMenu: 'legacy',
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 60,
    headerHeight: 40,
    defaultColDef: {
      sortable: true,
      resizable: true,
      editable: true,
      width: 190,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellEditorParams: {
        valuePlaceholder: ""
      },
      comparator: (valueA, valueB, nodeA, nodeB, isDescending) => nullSafeComparator(valueA, valueB, isDescending)
    },
    localeText: {
      blanks: ''
    },
    columnTypes: {
      numberColumn: {
        width: 150,
        valueFormatter: (data) => {
          if (typeof(data) === "number") {
            return NumberUtil.formatNumber(data, { maximumFractionDigits: 0, minimumFractionDigits: 0});
          }
          if (isNull(data?.value) || isUndefined(data?.value) || !isFinite(data?.value)) {
            return "";
          }
          return NumberUtil.formatNumberData(data, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
        }
      },
      currencyNumberColumn: {
        width: 150,
        valueFormatter: (data) => {
          if (isNull(data?.value) || isUndefined(data?.value) || !isFinite(data?.value)) {
            return "";
          }
          const formatValue = NumberUtil.formatNumberData(data, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
          return data.value >= 0 ? currencySymbol + formatValue : `-${currencySymbol}${formatValue.replace("-", "")}`;
        }
      },
      decimalPointColumn: {
        //Default Decimal Point: 2
        width: 150,
        valueFormatter: (data) => {
          if (isNull(data?.value) || isUndefined(data?.value) || !isFinite(data?.value)) {
            return "";
          }
          return NumberUtil.formatNumberData(data, { maximumFractionDigits: 2, minimumFractionDigits: 1 })
        }
      },
      rationalColumn: {
        width: 150,
        valueFormatter: (data) => {
          if (isNull(data?.value) || isUndefined(data?.value) || !isFinite(data?.value)) {
            return "";
          }
          return NumberUtil.formatNumberData(data, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
      },
      percentColumn: {
        width: 150,
        valueFormatter: (data) => {
          if (isNull(data?.value) || isUndefined(data?.value) || !isFinite(data?.value)) {
            return "";
          }
          return NumberUtil.formatNumberData(data, { style: "percent", maximumFractionDigits: 1, minimumFractionDigits: 1 })
        }
      },
      detailPercentColumn: {
        width: 150,
        valueFormatter: (data) => {
          if (isNull(data?.value) || isUndefined(data?.value) || !isFinite(data?.value)) {
            return "";
          }
          return NumberUtil.formatNumberData(data, { style: "percent", maximumFractionDigits: 3, minimumFractionDigits: 3 })
        }
      },
      nonEditableColumn: {
        editable: false
      },
      booleanColumn: {
        cellRenderer: params => {
          return params.node.rowPinned ? null : <input type="checkbox" defaultChecked={params.value} onClick={(e) => checkboxClickEvent(e, params)} />
        },
        onCellClicked: params => {
          if (isEditable(params)) {
            if (!params.node.rowPinned) {
              params.node.setDataValue(params.column, !params.value);
            }
          }
        }
      },
      defaultValueParser: {
        valueParser: (data) => {
          if (isNull(data?.newValue) || isUndefined(data?.newValue)) {
            return null;
          }
          return data.newValue;
        }
      },
      defaultValueFormatter: {
        valueFormatter: (data) => {
          if (isNull(data?.value) || isUndefined(data?.value)) {
            return "";
          }
          return data?.value;
        }
      },
    },
    getRowStyle: DataState.getRowStyles,
    getRowClass: DataState.getRowClass,
    rowSelection: {
      mode: 'multiRow',
      selectAll: 'filtered',
      checkboxes: false,
      headerCheckbox: false,
      enableClickSelection: true
    },
    onCellValueChanged,
    defaultExcelExportParams: GridExportSetting.defaultExcelExportParams(title),
    defaultCsvExportParams: GridExportSetting.defaultCsvExportParams(title),
    processCellForClipboard,
    cellSelection: true,
    suppressLastEmptyLineOnPaste: true,
    stopEditingWhenCellsLoseFocus: true,
    getMainMenuItems: params => getMainMenuItems(params, others),
    excelStyles,
    getContextMenuItems
  }
}

const DpDefaultGridSetting = ({ title = "", ...others }) => {
  return {
    theme: "legacy",
    columnMenu: 'legacy',
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 20,
    cellSelection: true,
    defaultColDef: {
      sortable: false,
      resizable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      filter: false,
      cellEditorParams: {
        valuePlaceholder: ""
      }
    },
    localeText: {
      blanks: ''
    },
    columnTypes: {
      numberColumn: {
        width: 150,
        filter: "agNumberColumnFilter",
        valueFormatter: data => NumberUtil.formatNumberData(data, { maximumFractionDigits: 2 }),
      },
      decimalPointColumn: {
        //Default Decimal Point: 2
        width: 150,
        filter: "agNumberColumnFilter",
        valueFormatter: (data) => {
          if (isNull(data?.value) || isUndefined(data?.value) || !isFinite(data?.value)) {
            return "";
          }
          return NumberUtil.formatNumberData(data, { maximumFractionDigits: 2 })
        }
      },
      percentColumn: {
        width: 150,
        filter: "agNumberColumnFilter",
        valueFormatter: data => NumberUtil.formatNumberData(data, { style: "percent", maximumFractionDigits: 2 }),
      },
      nonEditableColumn: {
        editable: false
      },
      defaultValueParser: {
        valueParser: (data) => {
          if (isNull(data?.newValue) || isUndefined(data?.newValue)) {
            return null;
          }
          return data.newValue;
        }
      },
      defaultValueFormatter: {
        valueFormatter: (data) => {
          if (isNull(data.value) || isUndefined(data.value)) {
            return "";
          }
          return data.value;
        }
      },
    },
    defaultExcelExportParams: GridExportSetting.defaultExcelExportParams(title),
    defaultCsvExportParams: GridExportSetting.defaultCsvExportParams(title),
    getContextMenuItems,
    getMainMenuItems: params => getMainMenuItems(params, others)
  }
}

export default DefaultGridSetting;
export { DpDefaultGridSetting, nullSafeComparator };